import { useI18n } from 'vue-i18n';
import { ApiHistoryEntry } from '@/rennovation/planning-view/types/api/history-response';
import { useDateTime } from '../../../../components/composables/useDateTime';

export function useHistoryFormatter() {
	const { t } = useI18n();
	const { formatDate, formatTime, formatDateTime } = useDateTime();

	const formatUserName = (user?: { firstName?: string; lastName?: string } | null, fallback = t('nobody')): string => {
		if (!user) return fallback;

		const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
		return fullName.trim() || fallback;
	};

	const formatDutyMessage = (entry: ApiHistoryEntry): string | null => {
		const { record: duty, event: eventType, updates, by } = entry;

		let assignedTo: string, assignedFrom: string;
		const dutyName = duty?.name;
		const assignedBy = typeof by === 'string' ? by : formatUserName(by, t('unknown_user'));
		const changeComment = updates?.changeComment || null;

		switch (eventType) {
			case 'created':
				assignedFrom = formatUserName(null);
				assignedTo = formatUserName(duty?.user);
				return changeComment
					? t('history.created_comment', {
							createdBy: assignedBy,
							dutyName,
							assignedTo,
							changeComment,
						})
					: t('history.created', { createdBy: assignedBy, dutyName, assignedTo });

			case 'updated':
				assignedFrom = formatUserName(duty?.user);
				assignedTo = formatUserName(updates?.user);
				if (updates?.user !== undefined) {
					return changeComment
						? t('history.updated_comment', {
								updatedBy: assignedBy,
								dutyName,
								assignedFrom,
								assignedTo,
								changeComment,
							})
						: t('history.updated', { updatedBy: assignedBy, dutyName, assignedFrom, assignedTo });
				}
				return null;

			case 'deleted':
				assignedFrom = formatUserName(duty?.lastAssignedUser);
				assignedTo = formatUserName(null);
				return changeComment
					? t('history.deleted_comment', {
							updatedBy: assignedBy,
							dutyName,
							assignedTo,
							changeComment,
						})
					: t('history.deleted', { updatedBy: assignedBy, dutyName, assignedTo });

			default:
				return null;
		}
	};

	return {
		formatDate,
		formatTime,
		formatDateTime,
		formatDutyMessage,
		formatUserName,
	};
}
